"use client";

import Link from "next/link";
import Image from "next/image";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Button from "react-bootstrap/Button";
import styles from "./nav.module.scss";
import { useAuth } from "@/providers/auth-context";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import clsx from "clsx";
import { useState, useRef, useEffect, useMemo } from "react";

import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getCurrentUser } from "./actions";
import Container from "react-bootstrap/Container";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsIcon from "@mui/icons-material/Settings";
import Cookies from "js-cookie";
import { API_BASE_URL } from "@/config/api";

const PRODUCT_SERVICES: { id: string; label: string; href: string }[] = [
  { id: "specific-yield", label: "Specific Yield", href: "/peatsense?source=specific-yield" },
  { id: "pfi-scorecard", label: "Emission Scoping Calculator", href: "/peatsense?source=pfi-scorecard" },
  { id: "maps", label: "Maps", href: "/map" },
  { id: "sentinel", label: "Sentinel-2 Imagery", href: "/peatsense?source=sentinel" },
  // { id: "sentinel1", label: "Sentinel-1 Imagery", href: "/peatsense?source=sentinel1" },
  { id: "safe-to-geotiff", label: "SAFE to GeoTIFF", href: "/peatsense?source=safe-to-geotiff" },
  { id: "pixel-extraction", label: "S2 Extraction Service", href: "/peatsense?source=pixel-extraction" },
  { id: "sentinel2-wrapper", label: "Sentinel-2 Wrapper", href: "/peatsense?source=sentinel2-wrapper" },
  { id: "pixel-annotation", label: "Pixel Annotation & Cloud Masking", href: "/peatsense?source=pixel-annotation" },
  { id: "landcover-inference", label: "Model Inference", href: "/peatsense?source=landcover-inference" },
  { id: "radiometric-correction", label: "Radiometric Correction", href: "/peatsense?source=radiometric-correction" },
  { id: "vegetation-index", label: "Vegetation Index", href: "/peatsense?source=vegetation-index" },
  { id: "tif-upload", label: "Upload TIF to GCS", href: "/peatsense?source=tif-upload" },
  { id: "parquet-viewer", label: "Files Reader", href: "/peatsense?source=parquet-viewer" },
  { id: "data-catalog", label: "Data Catalog", href: "/peatsense?source=data-catalog" },
  { id: "multitemp-vis", label: "Multitemporal Visualisation", href: "/peatsense?source=multitemp-vis" },
];

const CONTACT_SERVICE = { id: "contact", label: "Request a Service", href: "/contact-us" };

const TopNav = () => {
  const { authenticated, logout, userName, setUserName } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [navLoading, setNavLoading] = useState(false);
  const [navLoadingLabel, setNavLoadingLabel] = useState("");
  const navLoadingSafetyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [peatsenseSubmenuOpen, setPeatsenseSubmenuOpen] = useState(false);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [isAdminAvailable, setIsAdmin] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isServiceStaff, setIsServiceStaff] = useState(false);
  const [visibleServiceIds, setVisibleServiceIds] = useState<string[]>([]);
  const [offeredServiceIds, setOfferedServiceIds] = useState<string[]>(
    PRODUCT_SERVICES.map((s) => s.id),
  );
  const productHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peatsenseSubmenuHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const moreHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aboutHoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const allDropdowns: [
    (v: boolean) => void,
    React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  ][] = [
    [setProductDropdownOpen, productHoverTimeout],
    [setMoreDropdownOpen, moreHoverTimeout],
    [setAboutDropdownOpen, aboutHoverTimeout],
  ];

  const openOnHover = (
    setOpen: (v: boolean) => void,
    timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  ) => () => {
    allDropdowns.forEach(([otherSetOpen, otherTimeoutRef]) => {
      if (otherSetOpen === setOpen) return;
      if (otherTimeoutRef.current) {
        clearTimeout(otherTimeoutRef.current);
        otherTimeoutRef.current = null;
      }
      otherSetOpen(false);
    });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setOpen(true);
  };

  const closeOnHoverLeave = (
    setOpen: (v: boolean) => void,
    timeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
  ) => () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 250);
  };

  const openPeatsenseSubmenu = () => {
    if (peatsenseSubmenuHoverTimeout.current) {
      clearTimeout(peatsenseSubmenuHoverTimeout.current);
      peatsenseSubmenuHoverTimeout.current = null;
    }
    setPeatsenseSubmenuOpen(true);
  };

  const closePeatsenseSubmenu = () => {
    peatsenseSubmenuHoverTimeout.current = setTimeout(
      () => setPeatsenseSubmenuOpen(false),
      250,
    );
  };

  useEffect(() => {
    return () => {
      if (productHoverTimeout.current) clearTimeout(productHoverTimeout.current);
      if (peatsenseSubmenuHoverTimeout.current) clearTimeout(peatsenseSubmenuHoverTimeout.current);
      if (moreHoverTimeout.current) clearTimeout(moreHoverTimeout.current);
      if (aboutHoverTimeout.current) clearTimeout(aboutHoverTimeout.current);
      if (navLoadingSafetyTimeout.current) clearTimeout(navLoadingSafetyTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (!productDropdownOpen) setPeatsenseSubmenuOpen(false);
  }, [productDropdownOpen]);

  useEffect(() => {
    setNavLoading(false);
    if (navLoadingSafetyTimeout.current) {
      clearTimeout(navLoadingSafetyTimeout.current);
      navLoadingSafetyTimeout.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  const navigateWithLoader = (href: string, label: string) => {
    setProductDropdownOpen(false);
    setNavbarExpanded(false);

    const currentUrl = searchParams.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;

    if (href === currentUrl) {
      setNavLoading(false);
      return;
    }

    setNavLoadingLabel(label);
    setNavLoading(true);

    if (navLoadingSafetyTimeout.current) clearTimeout(navLoadingSafetyTimeout.current);
    navLoadingSafetyTimeout.current = setTimeout(() => setNavLoading(false), 12000);

    router.push(href);
  };

  const isHomePage = pathname === "/";
  const isAcknowledgementsPage = pathname === "/acknowledgements";
  const isHeroPage = isHomePage || isAcknowledgementsPage;
  const isLoginPage = pathname === "/login";

  useEffect(() => {
    if (!isHeroPage) {
      setScrolled(false);
      return;
    }
    const threshold = window.innerWidth < 768 ? 80 : window.innerHeight * 0.5;
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHeroPage]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const aboutDropdownRef = useRef<HTMLDivElement>(null);
  const moreDropdownRef = useRef<HTMLDivElement>(null);
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const capitalize = (str: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
  useEffect(() => {
    if (!authenticated) return;

    const run = async () => {
      try {
        const me = await getCurrentUser();
        if (!me) {
          logout();
          return;
        }
        setIsAdmin(me?.is_admin === true);
        const firstName = capitalize(me?.profile?.first_name ?? "");
        const name = firstName.trim() || "User";
        setUserName(name);
      } catch (e) {
        console.error("User fetch failed", e);
        logout();
      }
    };

    run();
  }, [authenticated, logout, setUserName]);

  useEffect(() => {
    if (!authenticated) {
      setIsServiceStaff(false);
      setVisibleServiceIds([]);
      (async () => {
        try {
          const res = await fetch(
            `${API_BASE_URL}/services-offered/public-services/`,
          );
          if (!res.ok) return;
          const data = await res.json();
          setOfferedServiceIds(
            Array.isArray(data?.offered_tabs)
              ? data.offered_tabs
              : PRODUCT_SERVICES.map((s) => s.id),
          );
        } catch {
        }
      })();

      return;
    }

    (async () => {
      try {
        const token = Cookies.get("token_access");
        const res = await fetch(
          `${API_BASE_URL}/services-offered/my-services/`,
          {
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          },
        );
        if (!res.ok) return;
        const data = await res.json();
        setIsServiceStaff(!!data?.is_staff);
        setVisibleServiceIds(Array.isArray(data?.tabs) ? data.tabs : []);
        setOfferedServiceIds(
          Array.isArray(data?.offered_tabs)
            ? data.offered_tabs
            : PRODUCT_SERVICES.map((s) => s.id),
        );
      } catch {
        setIsServiceStaff(false);
        setVisibleServiceIds([]);
      }
    })();
  }, [authenticated]);

  const isServiceActive = (id: string) =>
    isServiceStaff || visibleServiceIds.includes(id);

  const orderedProductServices = useMemo(() => {
    const offered = isServiceStaff
      ? PRODUCT_SERVICES
      : PRODUCT_SERVICES.filter((s) => offeredServiceIds.includes(s.id));
    const active = offered.filter((s) => isServiceActive(s.id));
    const inactive = offered.filter((s) => !isServiceActive(s.id));
    return [...active, ...inactive];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isServiceStaff, visibleServiceIds, offeredServiceIds]);

  const isActive = (href: string, exact = false) => {
    const norm = (p: string) =>
      p !== "/" && p.endsWith("/") ? p.slice(0, -1) : p;

    const path = norm(pathname);
    const link = norm(href);

    if (exact) return path === link;
    return path === link || path.startsWith(`${link}/`);
  };

  const NavItem = ({
    href,
    label,
    exact = false,
    onClick,
  }: {
    href: string;
    label: string;
    exact?: boolean;
    onClick?: () => void;
  }) => {
    const active = isActive(href, exact);
    return (
      <Nav.Link
        href={href}
        aria-current={active ? "page" : undefined}
        className={clsx(styles.navLink, active && styles.active)}
        onClick={() => {
          setNavbarExpanded(false);
          onClick?.();
        }}
      >
        {label}
      </Nav.Link>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }

      if (
        aboutDropdownRef.current &&
        !aboutDropdownRef.current.contains(event.target as Node)
      ) {
        setAboutDropdownOpen(false);
      }

      if (
        moreDropdownRef.current &&
        !moreDropdownRef.current.contains(event.target as Node)
      ) {
        setMoreDropdownOpen(false);
      }

      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target as Node)
      ) {
        setProductDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    setNavbarExpanded(false);
  };

  return (
    <>
      {navLoading && (
        <div className={styles.navLoadingOverlay} role="status" aria-live="polite">
          <div className={styles.navLoadingCard}>
            <div className={styles.navLoadingHeader}>
              <span className={styles.navLoadingSpinner}></span>
              <span className={styles.navLoadingText}>
                Opening {navLoadingLabel || "service"}…
              </span>
            </div>
            <div className={styles.navLoadingSkeletonRow}></div>
            <div className={styles.navLoadingSkeletonRow}></div>
            <div className={styles.navLoadingSkeletonRow}></div>
          </div>
        </div>
      )}
    <Navbar
      collapseOnSelect
      expand="md"
      className={clsx(
        styles.topnav,
        (isLoginPage || (isHeroPage && !scrolled)) && styles.transparent,
        isHeroPage && scrolled && styles.scrolled,
      )}
      expanded={navbarExpanded}
      onToggle={(expanded) => setNavbarExpanded(expanded)}
    >
      <Container fluid className={styles.navContainer}>
        <div className={styles.navInner}>
          <Navbar.Brand href="/" className={styles.navBrand}>
            <div className={styles.brandWrapper}>
              <Image
                src="/logo_black.svg"
                priority
                width={0}
                height={0}
                sizes="100vw"
                className={styles.logo}
                alt="AI2Peat"
              />
              <span className={styles.brandText}>AI2Peat</span>
            </div>
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls="responsive-navbar-nav"
            className={styles.navbarToggle}
          />
          <Navbar.Collapse
            id="responsive-navbar-nav"
            className={styles.navbarCollapse}
          >
            <Nav className={styles.navLinks}>
              <NavItem href="/" label="Home" exact />
              <div
                className={styles.dropdownWrapper}
                ref={productDropdownRef}
                onMouseEnter={openOnHover(setProductDropdownOpen, productHoverTimeout)}
                onMouseLeave={closeOnHoverLeave(setProductDropdownOpen, productHoverTimeout)}
              >
                <button
                  className={clsx(
                    styles.navLink,
                    styles.dropdownToggle,
                    (isActive("/peatsense") || isActive("/map") || (!authenticated && isActive("/demo"))) && styles.active,
                  )}
                  style={{
                    fontWeight: "var(--bs-nav-link-font-size)",
                    fontSize: "var(--bs-nav-link-font-weight)",
                  }}
                  onClick={() => setProductDropdownOpen(!productDropdownOpen)}
                  aria-expanded={productDropdownOpen}
                >
                  Product
                  <ArrowDropDownIcon className={styles.dropdownIcon} />
                </button>

                {productDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    <div
                      className={styles.peatsenseMenuItem}
                      onMouseEnter={openPeatsenseSubmenu}
                      onMouseLeave={closePeatsenseSubmenu}
                    >
                      <div className={clsx(styles.dropdownItem, styles.peatsenseToggle)}>
                        <Link
                          href={authenticated ? "/peatsense" : "/demo"}
                          className={styles.peatsenseLabelLink}
                          onClick={(e) => {
                            e.preventDefault();
                            setPeatsenseSubmenuOpen(false);
                            setProductDropdownOpen(false);
                            if (authenticated) {
                              navigateWithLoader("/peatsense", "Peatsense");
                            } else {
                              navigateWithLoader("/demo", "Demo");
                            }
                          }}
                        >
                          Peatsense
                        </Link>
                        <button
                          type="button"
                          aria-label="Show Peatsense services"
                          className={styles.peatsenseCaretButton}
                          onClick={() => setPeatsenseSubmenuOpen((v) => !v)}
                          aria-expanded={peatsenseSubmenuOpen}
                        >
                          <ArrowDropDownIcon
                            className={clsx(styles.dropdownIcon, styles.peatsenseSubmenuIcon)}
                          />
                        </button>
                      </div>

                      {peatsenseSubmenuOpen && (
                        <div className={styles.submenu}>
                          {orderedProductServices.map((service) => {
                            const active = isServiceActive(service.id);

                            if (active) {
                              return (
                                <Link
                                  key={service.id}
                                  href={service.href}
                                  className={styles.dropdownItem}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setPeatsenseSubmenuOpen(false);
                                    navigateWithLoader(service.href, service.label);
                                  }}
                                >
                                  {service.label}
                                </Link>
                              );
                            }

                            return (
                              <div
                                key={service.id}
                                className={styles.disabledServiceWrapper}
                              >
                                <span
                                  className={clsx(
                                    styles.dropdownItem,
                                    styles.dropdownItemDisabled,
                                  )}
                                >
                                  {service.label}
                                </span>
                                <div className={styles.disabledTooltip}>
                                  Contact us to learn about this service
                                </div>
                              </div>
                            );
                          })}

                          <div className={styles.dropdownDivider}></div>

                          <Link
                            href={CONTACT_SERVICE.href}
                            className={styles.dropdownItem}
                            onClick={() => {
                              setProductDropdownOpen(false);
                              setPeatsenseSubmenuOpen(false);
                              setNavbarExpanded(false);
                            }}
                          >
                            {CONTACT_SERVICE.label}
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div
                className={styles.dropdownWrapper}
                ref={moreDropdownRef}
                onMouseEnter={openOnHover(setMoreDropdownOpen, moreHoverTimeout)}
                onMouseLeave={closeOnHoverLeave(setMoreDropdownOpen, moreHoverTimeout)}
              >
                <button
                  className={clsx(
                    styles.navLink,
                    styles.dropdownToggle,
                    (isActive("/directory") ||
                      isActive("/knowledge-hub") ||
                      isActive("/glossary")) &&
                      styles.active,
                  )}
                  style={{
                    fontWeight: "var(--bs-nav-link-font-size)",
                    fontSize: "var(--bs-nav-link-font-weight)",
                  }}
                  onClick={() => setMoreDropdownOpen(!moreDropdownOpen)}
                  aria-expanded={moreDropdownOpen}
                >
                  More
                  <ArrowDropDownIcon className={styles.dropdownIcon} />
                </button>

                {moreDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    <Link
                      href="/knowledge-hub"
                      className={clsx(
                        styles.dropdownItem,
                        isActive("/knowledge-hub") && styles.active,
                      )}
                      onClick={() => {
                        setMoreDropdownOpen(false);
                        setNavbarExpanded(false);
                      }}
                    >
                      Knowledge Hub
                    </Link>

                    <Link
                      href="/glossary"
                      className={clsx(
                        styles.dropdownItem,
                        isActive("/glossary") && styles.active,
                      )}
                      onClick={() => {
                        setMoreDropdownOpen(false);
                        setNavbarExpanded(false);
                      }}
                    >
                      Glossary
                    </Link>

                    {/* <Link
                      href="/news"
                      className={clsx(
                        styles.dropdownItem,
                        isActive("/news") && styles.active,
                      )}
                      onClick={() => {
                        setMoreDropdownOpen(false);
                        setNavbarExpanded(false);
                      }}
                    >
                      News
                    </Link> */}
                  </div>
                )}
              </div>
              <div
                className={styles.dropdownWrapper}
                ref={aboutDropdownRef}
                onMouseEnter={openOnHover(setAboutDropdownOpen, aboutHoverTimeout)}
                onMouseLeave={closeOnHoverLeave(setAboutDropdownOpen, aboutHoverTimeout)}
              >
                <button
                  className={clsx(
                    styles.navLink,
                    styles.dropdownToggle,
                    (isActive("/about-us") ||
                      isActive("/acknowledgements") ||
                      isActive("/faq-peatsense") ||
                      pathname.includes("ai2peat")) &&
                      styles.active,
                  )}
                  style={{
                    fontWeight: "var(--bs-nav-link-font-size)",
                    fontSize: "var(--bs-nav-link-font-weight)",
                  }}
                  onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                  aria-expanded={aboutDropdownOpen}
                >
                  About
                  <ArrowDropDownIcon className={styles.dropdownIcon} />
                </button>

                {aboutDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    <Link
                      href="/about-us"
                      className={clsx(
                        styles.dropdownItem,
                        isActive("/about-us") && styles.active,
                      )}
                      onClick={() => {
                        setAboutDropdownOpen(false);
                        setNavbarExpanded(false);
                      }}
                    >
                      About Us
                    </Link>

                    <Link
                      href="/acknowledgements"
                      className={clsx(
                        styles.dropdownItem,
                        isActive("/acknowledgements") && styles.active,
                      )}
                      onClick={() => {
                        setAboutDropdownOpen(false);
                        setNavbarExpanded(false);
                      }}
                    >
                      Acknowledgements
                    </Link>

                    <Link
                      href="/faq-peatsense"
                      className={clsx(
                        styles.dropdownItem,
                        isActive("/faq-peatsense") && styles.active,
                      )}
                      onClick={() => {
                        setAboutDropdownOpen(false);
                        setNavbarExpanded(false);
                      }}
                    >
                      FAQ
                    </Link>
                    <Link
                      href="/careers"
                      className={clsx(
                        styles.dropdownItem,
                        isActive("/careers") && styles.active,
                      )}
                      onClick={() => {
                        setAboutDropdownOpen(false);
                        setNavbarExpanded(false);
                      }}
                    >
                      Careers
                    </Link>
                  </div>
                )}
              </div>
            </Nav>

            <Nav className={styles.navRight}>
              <Link
                href="/demo"
                className={styles.demoBtn}
                onClick={() => setNavbarExpanded(false)}
              >
                Try the live demo
              </Link>

              <Link
                href="/contact-us"
                className={styles.contactUsBtn}
                onClick={() => setNavbarExpanded(false)}
              >
                Contact us
              </Link>

              {authenticated === true && (
                <div className={styles.profileDropdown} ref={dropdownRef}>
                  <button
                    className={styles.profileButton}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    aria-expanded={dropdownOpen}
                    aria-label="User profile menu"
                  >
                    <div className={styles.profileContent}>
                      <PersonIcon className={styles.profileIcon} />
                      <span className={styles.profileName}>
                        {userName || ""}
                      </span>
                      <ArrowDropDownIcon className={styles.dropdownArrow} />
                    </div>
                  </button>

                  {dropdownOpen && (
                    <div className={styles.dropdownMenu}>
                      <div className={styles.dropdownHeader}>
                        <AccountCircleIcon className={styles.dropdownIcon} />
                        <div className={styles.userInfo}>
                          <span className={styles.userName}>
                            {userName || ""}
                          </span>
                        </div>
                      </div>

                      <div className={styles.dropdownDivider}></div>

                      <Link
                        href="/profile"
                        className={styles.dropdownItem}
                        onClick={() => {
                          setDropdownOpen(false);
                          setNavbarExpanded(false);
                        }}
                      >
                        <PersonIcon className={styles.dropdownItemIcon} />
                        <span>My Profile</span>
                      </Link>
                      <div className={styles.dropdownDivider}></div>

                      {isAdminAvailable && (
                        <>
                          <div className={styles.dropdownDivider}></div>

                          <Link
                            href="/admin/layer-access"
                            className={styles.dropdownItem}
                            onClick={() => {
                              setDropdownOpen(false);
                              setNavbarExpanded(false);
                            }}
                          >
                            <AdminPanelSettingsIcon
                              className={styles.dropdownItemIcon}
                            />
                            <span>Admin Panel</span>
                          </Link>
                        </>
                      )}

                      {/* Commented out to remove settings from top nav menu 15-10-26 */}
                      {/* <div className={styles.dropdownDivider}></div> */}

                      {/* <Link
                        href="/settings"
                        className={styles.dropdownItem}
                        onClick={() => {
                          setDropdownOpen(false);
                          setNavbarExpanded(false);
                        }}
                      >
                        <SettingsIcon className={styles.dropdownItemIcon} />
                        <span>Settings</span>
                      </Link> */}

                      <div className={styles.dropdownDivider}></div>

                      <button
                        className={`${styles.dropdownItem} ${styles.logoutItem}`}
                        onClick={handleLogout}
                      >
                        <LogoutIcon className={styles.dropdownItemIcon} />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {authenticated === false && (
                <div className={styles.authButtons}>
                  <Link href="/login" passHref>
                    <Button
                      as="span"
                      variant="primary"
                      className={styles.loginButton}
                      onClick={() => setNavbarExpanded(false)}
                    >
                      Get Started
                    </Button>
                  </Link>
                </div>
              )}
            </Nav>
          </Navbar.Collapse>
        </div>
      </Container>
    </Navbar>
    </>
  );
};

export default TopNav;
